import { PagedEntityListOfTimeEntryListRM, TimeEntryRM } from '@herbjoy/apex-api-models';
import { IHttpService, IQService } from 'angular';
import moment from 'moment';

export class TimeEntryService {
    constructor(private $http: IHttpService, private $q: IQService, private apexApiUrl: string) {
        'ngInject'
    }

    /**
     * Retrieve all time entries matching the given filters.
     * @param {any} pageIndex
     * @param {any} query
     */
    retrieveTimeEntries(pageIndex, query) {
        if (query.StartDate) {
            query.StartDate = kendo.format("{0:yyyy-MM-dd}", query.StartDate);
        }
        if (query.EndDate) {
            query.EndDate = kendo.format("{0:yyyy-MM-dd}", query.EndDate);
        }
        query.PageIndex = pageIndex || 0;
        return this.$http<PagedEntityListOfTimeEntryListRM>({
            method: "GET",
            headers: { Accept: "application/json" },
            url: this.apexApiUrl + "time-entries",
            params: query
        })
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Obsolete. Retrieve time entries matching the given start date, employee, and approval status.
     * @param {any} startDate
     * @param {any} filteredEmployeeID
     * @param {any} unapprovedOnly
     */
    retrieveTimeEntriesByWeek(startDate, filteredEmployeeID, unapprovedOnly) {
        return this.$http({
            method: "GET",
            url: this.apexApiUrl + "time-entries/week/" + kendo.format("{0:yyyy-MM-dd}", startDate)
        })
    }

    /**
     * Get the time entry with the given ID.
     * @param {any} timeEntryID
     */
    retrieveTimeEntry(timeEntryID) {
        return this.$http.get<TimeEntryRM>(this.apexApiUrl + "/time-entries/" + timeEntryID)
            .then(function (response) {
                return response.data;
            });
    }

    /**
     * Save the given time entry.
     * @param {any} timeEntry
     */
    saveTimeEntry(timeEntry) {
        if (timeEntry.Id) {
            return this.updateTimeEntry(timeEntry.Id, timeEntry);
        } else {
            return this.createTimeEntry(timeEntry);
        }
    }

    private createTimeEntry(timeEntry) {
        const offset = moment(timeEntry.Date).utcOffset() / 60;
        const createRequestBase = {
            Date: timeEntry.Date,
            EmployeeID: timeEntry.EmployeeID,
            TimeEntryTypeID: timeEntry.TimeEntryTypeID,
            IcdOfficeID: timeEntry.IcdOfficeID,
            ProgramID: timeEntry.ProgramId,
            StoreID: timeEntry.LocationId,
            Comments: timeEntry.Comments,
            IsRealTime: false,
            TimeZoneOffset: offset
        };
        const requests = [];
        if (timeEntry.WorkStartEndTime.Start && timeEntry.WorkStartEndTime.End) {
            requests.push({
                ...createRequestBase,
                // Times need to be in 24h format to parse into TimeSpans correctly.
                StartTime: timeEntry.WorkStartEndTime.Start.toString("HH:mm:ss"),
                EndTime: timeEntry.WorkStartEndTime.End.toString("HH:mm:ss"),
                IsBreak: false
            })
        }
        if (timeEntry.BreakStartEndTime.Start && timeEntry.BreakStartEndTime.End) {
            requests.push({
                ...createRequestBase,
                StartTime: timeEntry.BreakStartEndTime.Start.toString("HH:mm:ss"),
                EndTime: timeEntry.BreakStartEndTime.End.toString("HH:mm:ss"),
                IsBreak: true
            });
        }
        timeEntry.AdditionalTimeEntries.forEach(function (additional) {
            requests.push({
                ...createRequestBase,
                StartTime: additional.StartEndTime.Start.toString("HH:mm:ss"),
                EndTime: additional.StartEndTime.End.toString("HH:mm:ss"),
                IsBreak: additional.IsBreak,
                TimeEntryType: additional.TimeEntryTypeID,
                Comments: additional.Comments
            })
        })

        const postRequest = request => this.$http<any>({
            method: "POST",
            url: this.apexApiUrl + "time-entries",
            headers: { "Content-Type": "application/json" },
            data: request
        });

        // Create the first time entry first and link the others as its parent.
        return postRequest(requests.shift())
            .then(response => {
                const id = response.data.Id;
                requests.forEach(function (request) {
                    request.ParentTimeEntryID = id;
                });
                return this.$q.all(requests.map(postRequest))
                    .then(function (v) {
                        timeEntry.AdditionalTimeEntries = v.map(response => response.data)
                    });
            });
    }

    createTimeEntryRecord(request) {
        return this.$http({
            method: "POST",
            url: this.apexApiUrl + "v2/time-entries",
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }


    updateTimeEntryRecord(timentryID, request) {
        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "v2/time-entries/" + timentryID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    private updateTimeEntry(timeEntryID, timeEntry) {
        // A time entry can have both a work component and a meal break component.
        // Only one of those two will correspond with the timeEntry ID, so this "update" might require the creation of a
        // new time entry if both times are filled in.
        const updateRequest = {
            ProgramID: timeEntry.ProgramId,
            StoreID: timeEntry.LocationId,
            IcdOfficeID: timeEntry.IcdOfficeID,
            EmployeeID: timeEntry.EmployeeID,
            Date: timeEntry.Date,
            Comments: timeEntry.Comments,
            TimeEntryTypeID: timeEntry.TimeEntryTypeID,
            IsRealTime: false // Changes from the web app are never real-time.
        };
        let createRequest = null;
        if (!!timeEntry.WorkStartEndTime.Start !== !!timeEntry.BreakStartEndTime.Start) {
            // One or the other is null (assuming the time entry is valid and at least one is filled in), so just perform an update.
            Object.assign(updateRequest, {
                StartTime: (timeEntry.WorkStartEndTime.Start || timeEntry.BreakStartEndTime.Start).toString("HH:mm:ss"),
                EndTime: (timeEntry.WorkStartEndTime.End || timeEntry.BreakStartEndTime.End).toString("HH:mm:ss"),
                IsBreak: !!timeEntry.BreakStartEndTime.Start, // The break is filled in.
                // Use the original time zone offsets for the time entry
                TimeZoneOffset: timeEntry.WorkStartEndTime.TimeZoneOffset
                    || timeEntry.BreakStartEndTime.TimeZoneOffset
            });
        } else {
            // Issue an update request for either the meal break or work interval depending on whether IsBreak is true.
            const updateInterval = timeEntry.IsBreak ? timeEntry.BreakStartEndTime : timeEntry.WorkStartEndTime;
            const createInterval = !timeEntry.IsBreak ? timeEntry.BreakStartEndTime : timeEntry.WorkStartEndTime;
            createRequest = Object.assign({
                ParentTimeEntryID: timeEntryID,
                StartTime: createInterval.Start.toString("HH:mm:ss"),
                EndTime: createInterval.End.toString("HH:mm:ss"),
                IsBreak: !timeEntry.IsBreak,
                // Use the same time zone offset as the existing entry as the one that is being created.
                TimeZoneOffset: updateInterval.TimeEntryService
            }, updateRequest)
            Object.assign(updateRequest, {
                StartTime: updateInterval.Start.toString("HH:mm:ss"),
                EndTime: updateInterval.End.toString("HH:mm:ss"),
                IsBreak: timeEntry.IsBreak,
                TimeZoneOffset: updateInterval.TimeZoneOffset
            });
        }

        return this.$http({
            method: "PUT",
            url: this.apexApiUrl + "time-entries/" + timeEntryID,
            headers: { "Content-Type": "application/json" },
            data: updateRequest
        }).then(() => {
            // Wait until the parent update is complete before pushing any
            // child requests.
            const requests = [];
            if (createRequest) {
                requests.push(this.$http({
                    method: "POST",
                    url: this.apexApiUrl + "time-entries",
                    headers: { "Content-Type": "application/json" },
                    data: createRequest
                }));
            }
            timeEntry.AdditionalTimeEntries.forEach(additional => {
                const data = {
                    ParentTimeEntryID: timeEntryID,
                    StartTime: additional.StartEndTime.Start.toString("HH:mm:ss"),
                    EndTime: additional.StartEndTime.End.toString("HH:mm:ss"),
                    TimeZoneOffset: additional.StartEndTime.TimeZoneOffset,
                    IsBreak: additional.IsBreak,
                    TimeEntryTypeID: additional.TimeEntryTypeID,
                    Comments: additional.Comments,
                    //TODO: Remove these validation requirements for child entries. These will not be used:
                    EmployeeID: timeEntry.EmployeeID,
                    IcdOfficeID: timeEntry.IcdOfficeID,
                    Date: timeEntry.Date
                };
                if (additional.IsNew) {
                    requests.push(this.$http({
                        method: "POST",
                        url: this.apexApiUrl + "time-entries",
                        headers: { "Content-Type": "application/json" },
                        data: data
                    }));
                } else if (additional.IsDeleted) {
                    requests.push(this.$http({
                        method: "DELETE",
                        url: this.apexApiUrl + 'time-entries/' + additional.Id,
                        data: {
                            Comments: data.Comments,
                        },
                        headers: { "Content-Type": "application/json" }
                    }));
                } else {
                    requests.push(this.$http({
                        method: "PUT",
                        url: this.apexApiUrl + "time-entries/" + additional.Id,
                        headers: { "Content-Type": "application/json" },
                        data: data
                    }));
                }
            });

            return this.$q.all(requests);

        });

    }

    patchTimeEntry(timeEntryID, patchRequest) {
        return this.$http({
            method: "PATCH",
            url: this.apexApiUrl + "time-entries/" + timeEntryID,
            headers: { "Content-Type": "application/json" },
            data: patchRequest
        });
    }

    deleteTimeEntry(timeEntryID, request) {
        return this.$http({
            method: "DELETE",
            url: this.apexApiUrl + "time-entries/" + timeEntryID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    /**
     * Get the list of all time entry types that can be assigned to a time entry.
     */
    retrieveTimeEntryTypes() {
        return this.$http({
            method: "GET",
            url: this.apexApiUrl + "time-entry-types/",
            responseType: "application/json"
        }).then(function (response) {
            return response.data;
        });
    }
}
